import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  ChevronLeft, ChevronRight, Plus, RefreshCw, AlertTriangle, Trash2,
  CalendarRange, CalendarDays, Copy, ChevronDown, Layers, Users
} from 'lucide-react';
import { 
  format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, isSameDay,
  startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, isSameMonth,
  parseISO
} from 'date-fns';
import { toast } from 'sonner';
import BulkScheduleDialog from '../../components/schedules/BulkScheduleDialog';
import WeeklyTemplateDialog from '../../components/schedules/WeeklyTemplateDialog';
import CopyDuplicateDialog from '../../components/schedules/CopyDuplicateDialog';

const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM to 9 PM

// Helper function to parse date strings consistently without timezone shift
// This ensures "2026-03-07" is treated as local date, not UTC
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  // Parse as local date by splitting and creating explicitly
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Weekday shifts (Monday-Friday)
const WEEKDAY_SHIFTS = [
  { value: 'AM', label: 'AM Shift', defaultStart: '09:00', defaultEnd: '17:00', target: 2, color: 'bg-emerald-50 border-l-4 border-emerald-500' },
  { value: 'Closing', label: 'Closing Shift', defaultStart: '11:30', defaultEnd: '19:30', target: 2, color: 'bg-rose-50 border-l-4 border-rose-500' }
];

// Saturday shift
const SATURDAY_SHIFT = { value: 'Saturday', label: 'Saturday Shift', defaultStart: '08:30', defaultEnd: '12:30', target: 2, color: 'bg-violet-50 border-l-4 border-violet-500' };

// Get shift options based on selected day
const getShiftOptionsForDay = (date) => {
  if (!date) return WEEKDAY_SHIFTS;
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  if (dayOfWeek === 6) return [SATURDAY_SHIFT]; // Saturday
  if (dayOfWeek === 0) return []; // Sunday - no shifts
  return WEEKDAY_SHIFTS; // Weekday
};

// Combined for display purposes
const ALL_SHIFT_TYPES = [...WEEKDAY_SHIFTS, SATURDAY_SHIFT];

export default function EmployeeSchedule({ embedded = false }) {
  const { api, isAdmin, user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [schedules, setSchedules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmployee, setFilterEmployee] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [copyDuplicateDialogOpen, setCopyDuplicateDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null);
  
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    shift_bucket: 'AM',
    start_time: '09:00',
    end_time: '17:00',
    assigned_to: '',
    notes: ''
  });

  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return { start: calendarStart, end: calendarEnd };
    } else {
      return { start: currentDate, end: currentDate };
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    fetchData();
  }, [dateRange, filterEmployee]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const schedulesRes = await api.get('/schedules', {
        params: {
          start_date: format(dateRange.start, 'yyyy-MM-dd'),
          end_date: format(dateRange.end, 'yyyy-MM-dd'),
          schedule_type: 'EMPLOYEE'
        }
      });

      let filteredSchedules = schedulesRes.data;
      if (filterEmployee !== 'all') {
        filteredSchedules = filteredSchedules.filter(s => s.assigned_to === filterEmployee);
      }
      setSchedules(filteredSchedules);

      if (isAdmin) {
        try {
          const usersRes = await api.get('/users?status=active');
          setEmployees(usersRes.data.filter(u => u.role === 'employee'));
        } catch (adminError) {
          console.log('Admin data fetch failed:', adminError);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };

  const navigate = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else {
      setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : addDays(currentDate, -1));
    }
  };

  const getSchedulesForDate = (date) => {
    return schedules.filter(s => {
      const scheduleDate = parseLocalDate(s.date);
      return isSameDay(scheduleDate, date);
    });
  };

  const getEmployeeName = (id) => {
    const employee = employees.find(e => e.id === id);
    if (employee) return `${employee.first_name} ${employee.last_name?.charAt(0)}.`;
    const schedule = schedules.find(s => s.assigned_to === id);
    return schedule?.title?.split(' - ')[0] || 'Employee';
  };

  const getShiftColor = (bucket) => {
    const shift = ALL_SHIFT_TYPES.find(s => s.value === bucket);
    return shift?.color || 'bg-slate-100 border-l-4 border-slate-400';
  };

  const checkConflicts = (employeeId, date, excludeScheduleId = null) => {
    const dateToCheck = typeof date === 'string' ? parseLocalDate(date) : date;
    return schedules.filter(s => 
      s.assigned_to === employeeId && 
      isSameDay(parseLocalDate(s.date), dateToCheck) &&
      s.id !== excludeScheduleId
    );
  };

  const getStaffingStatus = (date) => {
    const daySchedules = getSchedulesForDate(date);
    const dayOfWeek = date.getDay();
    const shiftsForDay = dayOfWeek === 6 ? [SATURDAY_SHIFT] : (dayOfWeek === 0 ? [] : WEEKDAY_SHIFTS);
    const status = shiftsForDay.map(bucket => {
      const count = daySchedules.filter(s => s.shift_bucket === bucket.value).length;
      return { ...bucket, count, met: count >= bucket.target };
    });
    return status;
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    // Use formData.date instead of selectedDate
    const scheduleDate = formData.date;

    // Check for conflicts
    const conflicts = checkConflicts(formData.assigned_to, scheduleDate);
    if (conflicts.length > 0) {
      setConflictWarning(`This employee already has ${conflicts.length} shift(s) on this day.`);
    }

    try {
      const employee = employees.find(e => e.id === formData.assigned_to);
      await api.post('/schedules', {
        title: `${employee?.first_name || 'Employee'} - ${formData.shift_bucket}`,
        start_time: formData.start_time,
        end_time: formData.end_time,
        event_type: 'EMPLOYEE_SHIFT',
        shift_bucket: formData.shift_bucket,
        assigned_to: formData.assigned_to,
        date: scheduleDate,
        notes: formData.notes
      });
      toast.success('Shift created successfully');
      setCreateDialogOpen(false);
      setConflictWarning(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    if (!selectedSchedule) return;
    setFormLoading(true);

    try {
      const employee = employees.find(e => e.id === formData.assigned_to);
      await api.patch(`/schedules/${selectedSchedule.id}`, {
        title: `${employee?.first_name || 'Employee'} - ${formData.shift_bucket}`,
        start_time: formData.start_time,
        end_time: formData.end_time,
        shift_bucket: formData.shift_bucket,
        assigned_to: formData.assigned_to,
        notes: formData.notes
      });
      toast.success('Shift updated successfully');
      setEditDialogOpen(false);
      setConflictWarning(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!selectedSchedule) return;
    setFormLoading(true);

    try {
      await api.delete(`/schedules/${selectedSchedule.id}`);
      toast.success('Shift deleted');
      setDeleteDialogOpen(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete shift');
    } finally {
      setFormLoading(false);
    }
  };

  const openCreateDialog = (date) => {
    setSelectedDate(date);
    setConflictWarning(null);
    const dateStr = format(date, 'yyyy-MM-dd');
    const shiftOptions = getShiftOptionsForDay(date);
    const defaultShift = shiftOptions[0] || WEEKDAY_SHIFTS[0];
    setFormData({
      date: dateStr,
      shift_bucket: defaultShift.value,
      start_time: defaultShift.defaultStart,
      end_time: defaultShift.defaultEnd,
      assigned_to: employees[0]?.id || '',
      notes: ''
    });
    setCreateDialogOpen(true);
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setConflictWarning(null);
    setFormData({
      date: schedule.date,
      shift_bucket: schedule.shift_bucket || 'AM',
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      assigned_to: schedule.assigned_to,
      notes: schedule.notes || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setDeleteDialogOpen(true);
  };

  const handleShiftChange = (bucket) => {
    const shift = ALL_SHIFT_TYPES.find(s => s.value === bucket);
    if (shift) {
      setFormData({
        ...formData,
        shift_bucket: bucket,
        start_time: shift.defaultStart,
        end_time: shift.defaultEnd
      });
    }
  };

  const handleDateChange = (newDate) => {
    const date = new Date(newDate);
    const shiftOptions = getShiftOptionsForDay(date);
    const currentShiftValid = shiftOptions.some(s => s.value === formData.shift_bucket);
    const newShift = currentShiftValid ? ALL_SHIFT_TYPES.find(s => s.value === formData.shift_bucket) : shiftOptions[0];
    
    setFormData({
      ...formData,
      date: newDate,
      shift_bucket: newShift?.value || 'AM',
      start_time: newShift?.defaultStart || '09:00',
      end_time: newShift?.defaultEnd || '17:00'
    });
  };

  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return [];
    return Array.from({ length: 7 }, (_, i) => addDays(dateRange.start, i));
  }, [dateRange, viewMode]);

  const monthDays = useMemo(() => {
    if (viewMode !== 'month') return [];
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [dateRange, viewMode]);

  // Render schedule event with edit/delete options
  const renderScheduleEvent = (schedule, compact = false) => (
    <div
      key={schedule.id}
      className={`p-2 rounded mb-1 ${getShiftColor(schedule.shift_bucket)} group relative cursor-pointer`}
      onClick={(e) => {
        e.stopPropagation();
        if (isAdmin) openEditDialog(schedule);
      }}
      data-testid={`shift-event-${schedule.id}`}
    >
      <p className={`font-medium ${compact ? 'text-xs' : 'text-sm'} truncate`}>
        {getEmployeeName(schedule.assigned_to)}
      </p>
      {!compact && (
        <p className="text-xs opacity-75">{schedule.shift_bucket} • {schedule.start_time}-{schedule.end_time}</p>
      )}
      {isAdmin && (
        <div className="absolute top-1 right-1 hidden group-hover:flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 bg-white/80 hover:bg-white"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteDialog(schedule);
            }}
          >
            <Trash2 className="h-3 w-3 text-red-500" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`space-y-6 ${embedded ? '' : ''}`} data-testid="employee-schedule-page">
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Employee Schedule</h1>
            <p className="text-slate-500 mt-1">Manage staff shifts and clinic coverage</p>
          </div>
        </div>
      )}

      {/* Admin Action Buttons - CLEAR AND SEPARATED */}
      {isAdmin && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-700 mr-2">Schedule Actions:</span>
              
              {/* Primary: Bulk Add */}
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => setBulkDialogOpen(true)}
                data-testid="bulk-employee-btn"
              >
                <CalendarRange className="h-4 w-4 mr-2" />
                Bulk Add (Date Range)
              </Button>
              
              {/* Secondary: Single Day */}
              <Button 
                variant="outline"
                onClick={() => openCreateDialog(currentDate)}
                data-testid="single-day-employee-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Single Day Shift
              </Button>
              
              {/* Weekly Templates */}
              <Button 
                variant="outline"
                onClick={() => setTemplateDialogOpen(true)}
                data-testid="weekly-template-btn"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Weekly Templates
              </Button>
              
              {/* More Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="more-actions-employee">
                    <Layers className="h-4 w-4 mr-2" />
                    More Actions
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setCopyDuplicateDialogOpen(true)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Day / Duplicate Week
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Use "Bulk Add" to schedule employees for weeks at once with repeat patterns. "Weekly Templates" to save and reuse schedule patterns.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Staffing Pattern Reference */}
      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-6 items-center">
            <p className="text-sm font-medium text-slate-700">Staffing Pattern:</p>
            {ALL_SHIFT_TYPES.map((shift) => (
              <div key={shift.value} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" 
                  style={{
                    backgroundColor: shift.value === 'AM' ? '#10b981' : shift.value === 'Saturday' ? '#8b5cf6' : '#f43f5e'
                  }}
                />
                <span className="text-sm text-slate-600">
                  {shift.label} ({shift.defaultStart}-{shift.defaultEnd}) - {shift.target} employees
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Calendar Controls */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigate('prev')} data-testid="nav-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate('next')} data-testid="nav-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold text-slate-900 ml-2">
                {viewMode === 'month' && format(currentDate, 'MMMM yyyy')}
                {viewMode === 'week' && `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`}
                {viewMode === 'day' && format(currentDate, 'EEEE, MMMM d, yyyy')}
              </h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant={viewMode === 'month' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('month')}
                data-testid="view-month"
              >
                Month
              </Button>
              <Button 
                variant={viewMode === 'week' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('week')}
                data-testid="view-week"
              >
                Week
              </Button>
              <Button 
                variant={viewMode === 'day' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('day')}
                data-testid="view-day"
              >
                Day
              </Button>
              {isAdmin && (
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger className="w-[150px]" data-testid="filter-employee">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name?.charAt(0)}.
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentDate(new Date())}
                data-testid="today-btn"
              >
                Today
              </Button>
              {isAdmin && embedded && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      className="bg-teal-600 hover:bg-teal-700"
                      size="sm"
                      data-testid="add-shift-dropdown"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openCreateDialog(currentDate)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Single Day Shift
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setBulkDialogOpen(true)}>
                      <CalendarRange className="h-4 w-4 mr-2" />
                      Bulk Add (Date Range)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setTemplateDialogOpen(true)}>
                      <CalendarDays className="h-4 w-4 mr-2" />
                      Weekly Templates
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setCopyDuplicateDialogOpen(true)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy / Duplicate
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : viewMode === 'month' ? (
            /* Month View */
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-7 bg-slate-50">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 border-b border-slate-200">
                    {day}
                  </div>
                ))}
              </div>
              {/* Days Grid */}
              <div className="grid grid-cols-7">
                {monthDays.map((day) => {
                  const daySchedules = getSchedulesForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isToday = isSameDay(day, new Date());
                  const staffingStatus = getStaffingStatus(day);
                  const hasUnmetTargets = staffingStatus.some(s => !s.met);
                  
                  return (
                    <div
                      key={day.toISOString()}
                      className={`min-h-[100px] p-1 border-b border-r border-slate-200 
                        ${!isCurrentMonth ? 'bg-slate-50 text-slate-400' : 'bg-white'}
                        ${isToday ? 'bg-teal-50' : ''}
                        ${hasUnmetTargets && isCurrentMonth ? 'bg-amber-50' : ''}
                        hover:bg-slate-50 cursor-pointer`}
                      onClick={() => isAdmin && openCreateDialog(day)}
                    >
                      <p className={`text-sm font-medium mb-1 ${isToday ? 'text-teal-600' : ''}`}>
                        {format(day, 'd')}
                      </p>
                      <div className="space-y-1">
                        {daySchedules.slice(0, 3).map(schedule => renderScheduleEvent(schedule, true))}
                        {daySchedules.length > 3 && (
                          <p className="text-xs text-slate-500">+{daySchedules.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : viewMode === 'week' ? (
            /* Week View - Hourly Grid */
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Header */}
                <div className="grid grid-cols-8 border-b border-slate-200">
                  <div className="p-3 text-sm font-medium text-slate-500">Time</div>
                  {weekDays.map((day) => {
                    const staffingStatus = getStaffingStatus(day);
                    const hasUnmetTargets = staffingStatus.some(s => !s.met);
                    
                    return (
                      <div 
                        key={day.toISOString()} 
                        className={`p-3 text-center border-l border-slate-200 ${isSameDay(day, new Date()) ? 'bg-teal-50' : ''}`}
                      >
                        <p className="text-sm font-medium text-slate-500">{format(day, 'EEE')}</p>
                        <p className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-teal-600' : 'text-slate-900'}`}>
                          {format(day, 'd')}
                        </p>
                        {hasUnmetTargets && (
                          <Badge className="mt-1 bg-amber-100 text-amber-700 text-xs">Understaffed</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Time Grid */}
                <div className="relative">
                  {HOURS.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-b border-slate-100">
                      <div className="p-2 text-xs text-slate-500 text-right pr-4">
                        {hour}:00
                      </div>
                      {weekDays.map((day) => {
                        const daySchedules = getSchedulesForDate(day).filter(s => {
                          const startHour = parseInt(s.start_time.split(':')[0]);
                          return startHour === hour;
                        });

                        return (
                          <div 
                            key={day.toISOString()} 
                            className="border-l border-slate-200 min-h-[60px] p-1 hover:bg-slate-50 cursor-pointer"
                            onClick={() => isAdmin && openCreateDialog(day)}
                          >
                            {daySchedules.map(schedule => renderScheduleEvent(schedule))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Day View - Fixed to show all shifts for the day */
            <div className="space-y-4">
              {/* Staffing Summary for Day */}
              <div className="flex flex-wrap gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                {getStaffingStatus(currentDate).map(status => (
                  <div key={status.value} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded" 
                      style={{
                        backgroundColor: status.value === 'AM' ? '#10b981' : status.value === 'Saturday' ? '#8b5cf6' : '#f43f5e'
                      }}
                    />
                    <span className={`text-sm ${status.met ? 'text-slate-600' : 'text-amber-600 font-medium'}`}>
                      {status.label}: {status.count}/{status.target}
                      {!status.met && ' (need more)'}
                    </span>
                  </div>
                ))}
              </div>

              {/* Show schedules grouped by shift type */}
              {(() => {
                const daySchedules = getSchedulesForDate(currentDate);
                const dayOfWeek = currentDate.getDay();
                const shiftsForDay = dayOfWeek === 6 ? [SATURDAY_SHIFT] : (dayOfWeek === 0 ? [] : WEEKDAY_SHIFTS);
                
                if (dayOfWeek === 0) {
                  return (
                    <div className="text-center py-12 text-slate-500">
                      <p className="text-lg font-medium">Sunday - Clinic Closed</p>
                      <p className="text-sm">No shifts scheduled on Sundays</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    {shiftsForDay.map(shiftType => {
                      const shiftSchedules = daySchedules.filter(s => s.shift_bucket === shiftType.value);
                      return (
                        <div key={shiftType.value} className="border rounded-lg overflow-hidden">
                          <div className={`p-3 ${shiftType.value === 'AM' ? 'bg-emerald-50' : shiftType.value === 'Saturday' ? 'bg-violet-50' : 'bg-rose-50'}`}>
                            <div className="flex justify-between items-center">
                              <div>
                                <h3 className="font-semibold">{shiftType.label}</h3>
                                <p className="text-sm text-slate-600">{shiftType.defaultStart} - {shiftType.defaultEnd}</p>
                              </div>
                              <Badge className={shiftSchedules.length >= shiftType.target ? 'bg-emerald-500' : 'bg-amber-500'}>
                                {shiftSchedules.length} / {shiftType.target} staff
                              </Badge>
                            </div>
                          </div>
                          <div className="p-3 bg-white">
                            {shiftSchedules.length === 0 ? (
                              <p className="text-slate-400 text-sm italic">No employees scheduled</p>
                            ) : (
                              <div className="space-y-2">
                                {shiftSchedules.map(schedule => renderScheduleEvent(schedule))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Also show any schedules that don't match expected buckets */}
                    {(() => {
                      const otherSchedules = daySchedules.filter(s => 
                        !shiftsForDay.some(shift => shift.value === s.shift_bucket)
                      );
                      if (otherSchedules.length > 0) {
                        return (
                          <div className="border rounded-lg overflow-hidden">
                            <div className="p-3 bg-slate-50">
                              <h3 className="font-semibold">Other Shifts</h3>
                            </div>
                            <div className="p-3 bg-white space-y-2">
                              {otherSchedules.map(schedule => renderScheduleEvent(schedule))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                );
              })()}
              
              {/* Add shift button at bottom */}
              {isAdmin && (
                <Button 
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  onClick={() => openCreateDialog(currentDate)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Shift for {format(currentDate, 'EEEE, MMM d')}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-50 border-l-4 border-emerald-500" />
          <span className="text-sm text-slate-600">AM Shift (9:00-17:00)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-rose-50 border-l-4 border-rose-500" />
          <span className="text-sm text-slate-600">Closing Shift (11:30-19:30)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-violet-50 border-l-4 border-violet-500" />
          <span className="text-sm text-slate-600">Saturday Shift (8:30-12:30)</span>
        </div>
      </div>

      {/* Create Shift Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Employee Shift</DialogTitle>
            <DialogDescription>
              Schedule a shift for an employee
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSchedule}>
            <div className="space-y-4 py-4">
              {conflictWarning && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <p className="text-sm text-amber-700">{conflictWarning}</p>
                </div>
              )}

              {/* Date Selection - Editable */}
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  data-testid="shift-date-input"
                />
                {formData.date && new Date(formData.date).getDay() === 0 && (
                  <p className="text-sm text-amber-600">Note: Sunday - Clinic is closed</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Shift Type</Label>
                <Select value={formData.shift_bucket} onValueChange={handleShiftChange}>
                  <SelectTrigger data-testid="shift-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getShiftOptionsForDay(new Date(formData.date)).map((shift) => (
                      <SelectItem key={shift.value} value={shift.value}>
                        {shift.label} ({shift.defaultStart}-{shift.defaultEnd})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select 
                  value={formData.assigned_to} 
                  onValueChange={(value) => {
                    setFormData({ ...formData, assigned_to: value });
                    const conflicts = checkConflicts(value, formData.date);
                    if (conflicts.length > 0) {
                      setConflictWarning(`This employee already has ${conflicts.length} shift(s) on this day.`);
                    } else {
                      setConflictWarning(null);
                    }
                  }}
                >
                  <SelectTrigger data-testid="employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    data-testid="start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    data-testid="end-time-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes..."
                  data-testid="notes-input"
                />
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> Need to schedule multiple dates or employees at once? Use the "Bulk Add" or "Weekly Templates" buttons above.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={formLoading || !formData.assigned_to}
                data-testid="create-shift-submit"
              >
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Create Shift'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Shift</DialogTitle>
            <DialogDescription>
              Modify shift details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSchedule}>
            <div className="space-y-4 py-4">
              {conflictWarning && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <p className="text-sm text-amber-700">{conflictWarning}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Shift Type</Label>
                <Select value={formData.shift_bucket} onValueChange={handleShiftChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_SHIFT_TYPES.map((shift) => (
                      <SelectItem key={shift.value} value={shift.value}>
                        {shift.label} ({shift.defaultStart}-{shift.defaultEnd})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Employee</Label>
                <Select 
                  value={formData.assigned_to} 
                  onValueChange={(value) => {
                    setFormData({ ...formData, assigned_to: value });
                    if (selectedSchedule) {
                      const conflicts = checkConflicts(value, new Date(selectedSchedule.date), selectedSchedule.id);
                      if (conflicts.length > 0) {
                        setConflictWarning(`This employee already has ${conflicts.length} other shift(s) on this day.`);
                      } else {
                        setConflictWarning(null);
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={formLoading}
              >
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shift</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this shift? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSchedule}
              className="bg-red-600 hover:bg-red-700"
            >
              {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Employee Schedule Dialog */}
      <BulkScheduleDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        scheduleType="employee"
        onSuccess={fetchData}
        currentDate={currentDate}
      />

      {/* Weekly Template Dialog */}
      <WeeklyTemplateDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        onSuccess={fetchData}
      />

      {/* Copy/Duplicate Dialog */}
      <CopyDuplicateDialog
        open={copyDuplicateDialogOpen}
        onOpenChange={setCopyDuplicateDialogOpen}
        onSuccess={fetchData}
        currentDate={currentDate}
        scheduleTypeFilter="EMPLOYEE"
      />
    </div>
  );
}
